import getConfig from "next/config";

import getIsomorphicApiRoot from "@/utils/get-isomorphic-api-root";
import { getRegistrationUrl } from "@/utils/get-registration-dashboard-url";
const { publicRuntimeConfig } = getConfig();

type RouteWithQuery = (queryParam: string | string[]) => string;

interface Config {
  HOPIN_ROOT: string;
  EVENTS_RING_CENTRAL: string;
  EVENTS_RING_CENTRAL_CONTACT: string;
  SIGN_IN: string;
  HOPIN_TOKEN: string;
  HOPIN_SSO_CHECK: string;
  HOPIN_SIGNIN: string;
  HOPIN_USER: string;
  HOPIN_BILLING: string;
  HOPIN_EVENTS: string;
  EVENT_REGISTRATION_ROUTE: RouteWithQuery;
  HOPIN_SSO_EVENT_LOGIN: (slug: string, queryParams: string) => string;
  HOPIN_SSO_GET_TOKEN: string;
  WHITELABEL_API: RouteWithQuery;
  EVENT_API_V2: (
    slug: string | string[],
    password?: string | string[]
  ) => string;
  EVENT_AUTH_DETAILS_API: (slug: string) => string;
  SPEAKERS_API: RouteWithQuery;
  VENDORS_API: RouteWithQuery;
  HOPIN_DASHBOARD_PATH: (slug: string, blockType: string) => string;
  HOPIN_ORGANIZER_EVENT_PREVIEW: (slug: string) => string;
  SPONSORS_API_V3: RouteWithQuery;
  HOPIN_PAGES_API_V2: (slug: string, pageId?: number) => string;
  SCHEDULES_API: RouteWithQuery;
  SCHEDULES_PUBLIC_EVENT_API: (slug: string) => string;
  TEXT_CUSTOMIZATIONS_API_V3: (slug: string) => string;
  PRESIGN_IMAGE_UPLOAD_API: (slug: string, filename: string) => string;
  EDIT_ROUTE: (
    slug: string | string[],
    params?: Record<string, string | string[]>
  ) => string;
  VIEW_ROUTE: RouteWithQuery;
  REGISTRATION_WIDGET: (slug: string) => string;
  LAUNCH_DARKLY: string;
  REGISTRATION_PAGE_SELECTION: RouteWithQuery;
  REGISTRATION_STATUS: (eventSlug: string) => string;
  REGISTRATION_RECEPTION_URL: (eventSlug: string) => string;
  REGISTRATION_VERIFY_SIMPLE_FLOW_AND_REGISTER: (slug: string) => string;
  REGISTRATION_SIMPLE_FLOW_VERIFY: (slug: string) => string;
  REGISTRATION_PUBLIC_EVENT_SETTINGS: (slug: string) => string;
  CUSTOM_FAVICON_PATH: (slug: string) => string;
  POLICY_URL: RouteWithQuery;
  NEW_LOGIN_URL: (slug: string, queryParams: string) => string;
  CUSTOM_FONTS_API: RouteWithQuery;
  CUSTOM_FONTS_CSS: (slug: string, pageId: string) => string;
  CUSTOM_FONT_FACE_API: (slug: string, fontId: string) => string;
  DELETE_CUSTOM_FONT_FACE_API: (
    slug: string,
    fontId: string,
    fontFaceId: string
  ) => string;
  FONT_IMPORT_API: (params: {
    slug: string;
    pageId: string;
    fontImportId?: string;
  }) => string;
  GET_RECORDINGS: (
    organizationExternalId: string,
    recordingsIds: string[],
    spaceExternalId?: string
  ) => string;
  CONTENT_HUB_EVENT_SUMMARIES: (
    organizationExternalId: string,
    eventExternalIds: string[]
  ) => string;
  NEXT_SERVER_GET_RECORDINGS: (recordingsIds: string[]) => string;
  INTERNAL_GET_SELECTED_RECORDINGS: (recordingsIds: string[]) => string;
  EVENT_SERIES: (eventExternalId: string) => string;
  EVENT_SERIES_EVENTS: (eventSeriesExternalId: string) => string;
}

export const HOPIN_ROOT = getIsomorphicApiRoot();

export const HOPIN_WEB_URL =
  publicRuntimeConfig && publicRuntimeConfig.HOPIN_WEB_URL
    ? publicRuntimeConfig.HOPIN_WEB_URL
    : "https://app.hopin.com";

const REGISTRATION_URL = getRegistrationUrl();

const MONOLITH_S3_URL = publicRuntimeConfig.MONOLITH_S3_URL;
const BASE_PATH = publicRuntimeConfig.BASE_PATH;

const endpoints: () => <K extends keyof Config>(key: K) => Config[K] = () => {
  const config = {
    HOPIN_ROOT,
    EVENTS_RING_CENTRAL: "https://events.ringcentral.com",
    EVENTS_RING_CENTRAL_CONTACT:
      "https://support.ringcentral.com/new-case-events.html",
    SIGN_IN: `${HOPIN_ROOT}/sign_in`,
    HOPIN_TOKEN: `${HOPIN_ROOT}/users/sso`,
    HOPIN_SSO_CHECK: `${HOPIN_ROOT}/users/check_sso_login`,
    HOPIN_SIGNIN: `${HOPIN_ROOT}/widget_sign_in`,
    HOPIN_USER: `${HOPIN_ROOT}/api/v1/public/users/introspect`,
    EVENT_REGISTRATION_ROUTE: (eventSlug: string) => `/events/${eventSlug}`,
    // TODO!: Make a dedicated endpoint for SSO login (when it exists)
    HOPIN_SSO_EVENT_LOGIN: (eventSlug: string, queryParams = "") =>
      `${HOPIN_ROOT}/events/${eventSlug}${queryParams}`,
    HOPIN_SSO_GET_TOKEN: `${HOPIN_ROOT}/api/v1/enterprise_sso/validate`,
    HOPIN_BILLING: `${HOPIN_ROOT}/organizations/billing`,
    HOPIN_EVENTS: `${HOPIN_ROOT}/organizations/events`,
    PREVIEW: (eventSlug) => `/events/${eventSlug}`,
    IMAGES_API: (organizationExternalId: string) =>
      `${HOPIN_ROOT}/api/v1/images/${organizationExternalId}/images`,
    TEXT_CUSTOMIZATIONS_API_V3: (slug: string) =>
      `${HOPIN_ROOT}/api/v3/page_builder/events/${slug}/locale`,
    PRESIGN_IMAGE_UPLOAD_API: (eventSlug, filename) =>
      `${HOPIN_ROOT}/api/v1/page_builder/events/${eventSlug}/images/presigned_url?filename=${filename}`,
    WHITELABEL_API: (customDomain) =>
      `${HOPIN_ROOT}/api/v1/page_builder/whitelabel/?custom_domain=${customDomain}`,
    EVENT_API_V2: (eventSlug: string, password?: string) => {
      const query = password ? `?password=${password}` : "";
      return `${HOPIN_ROOT}/api/v2/page_builder/events/${eventSlug}${query}`;
    },
    EVENT_AUTH_DETAILS_API: (eventSlug: string) =>
      `${HOPIN_ROOT}/api/v2/page_builder/events/${eventSlug}/auth_details`,
    SPEAKERS_API: (slug: string) =>
      `${HOPIN_ROOT}/api/v1/page_builder/events/${slug}/speakers`,
    VENDORS_API: (slug: string) =>
      `${HOPIN_ROOT}/api/v1/page_builder/events/${slug}/vendors`,
    SPONSORS_API_V3: (slug: string) =>
      `${HOPIN_ROOT}/api/v3/page_builder/events/${slug}/sponsors`,
    HOPIN_DASHBOARD_PATH: (slug: string, blockType: string) =>
      `${HOPIN_ROOT}/organisers/events/${slug}/${blockType}`,
    HOPIN_ORGANIZER_EVENT_PREVIEW: (slug: string) =>
      `${HOPIN_ROOT}/organisers/events/${slug}/preview`,
    HOPIN_PAGES_API_V2: (slug: string, pageId?: number) => {
      let url = `${HOPIN_ROOT}/api/v2/page_builder/events/${slug}/pages`;
      if (pageId !== undefined) {
        url += `/${pageId}`;
      }
      return url;
    },
    CUSTOM_FAVICON_PATH: (eventSlug: string) =>
      `${MONOLITH_S3_URL}/events/favicons/${eventSlug}/favicon.ico`,
    SCHEDULES_API: (slug: string) =>
      `${HOPIN_ROOT}/api/v1/page_builder/events/${slug}/schedules`,
    SCHEDULES_PUBLIC_EVENT_API: (slug: string) =>
      `${HOPIN_ROOT}/api/v2/schedules/public/${slug}/event`,
    EDIT_ROUTE: (slug, params) => {
      const queryParams =
        params === undefined
          ? ""
          : `?${new URLSearchParams(params)?.toString()}`;
      return `/events/${slug}/registration/edit${queryParams}`;
    },
    VIEW_ROUTE: (slug: string) => `/events/${slug}/registration`,
    NEW_LOGIN_URL: (slug: string, queryParams: string) =>
      `${BASE_PATH}/events/${slug}/new_login${queryParams}`,
    REGISTRATION_WIDGET: (slug: string) => {
      return `${REGISTRATION_URL}/widgets/registration/${slug}`;
    },
    REGISTRATION_PAGE_SELECTION: (eventSlug: string) =>
      `${REGISTRATION_URL}/registration-page?slug=${eventSlug}`,
    REGISTRATION_STATUS: (eventSlug: string) =>
      `${HOPIN_ROOT}/api/v1/registrations_component/${eventSlug}/tickets/registration_status`,
    REGISTRATION_RECEPTION_URL: (eventSlug: string) =>
      `${HOPIN_ROOT}/api/v1/registrations_component/${eventSlug}/tickets/reception_url`,
    REGISTRATION_VERIFY_SIMPLE_FLOW_AND_REGISTER: (eventSlug: string) =>
      `${HOPIN_ROOT}/api/v1/public/registrations_component/simple_flow/${eventSlug}`,
    REGISTRATION_SIMPLE_FLOW_VERIFY: (eventSlug: string) =>
      `${HOPIN_ROOT}/api/v1/public/registrations_component/simple_flow/${eventSlug}/verify`,
    REGISTRATION_PUBLIC_EVENT_SETTINGS: (eventSlug: string) =>
      `${HOPIN_ROOT}/api/v1/public/registrations_component/public_event_settings/${eventSlug}`,
    POLICY_URL: (eventSlug: string) =>
      `${HOPIN_ROOT}/api/v1/page_builder/events/${eventSlug}/images/presigned_url`,
    CUSTOM_FONTS_API: (eventSlug: string) =>
      `${HOPIN_ROOT}/api/v2/page_builder/events/${eventSlug}/fonts`,
    CUSTOM_FONTS_CSS: (eventSlug: string, pageId: string) =>
      `${HOPIN_ROOT}${BASE_PATH}/api/fonts/${eventSlug}/${pageId}`,
    CUSTOM_FONT_FACE_API: (eventSlug: string, fontId: string) =>
      `${HOPIN_ROOT}/api/v2/page_builder/events/${eventSlug}/fonts/${fontId}/font_faces`,
    DELETE_CUSTOM_FONT_FACE_API: (
      eventSlug: string,
      fontId: string,
      fontFaceId: string
    ) =>
      `${HOPIN_ROOT}/api/v2/page_builder/events/${eventSlug}/fonts/${fontId}/font_faces/${fontFaceId}`,
    FONT_IMPORT_API: ({
      slug,
      pageId,
      fontImportId,
    }: {
      slug: string;
      pageId: string;
      fontImportId?: string;
    }) => {
      let url = `${HOPIN_ROOT}/api/v2/page_builder/events/${slug}/pages/${pageId}/font_imports`;

      if (fontImportId !== undefined) {
        url += `/${fontImportId}`;
      }
      return url;
    },
    GET_RECORDINGS: (organizationExternalId, recordingIds, spaceExternalId) => {
      const queryParams = new URLSearchParams();
      queryParams.append("organization_id", organizationExternalId);
      recordingIds.forEach((id) => {
        queryParams.append("recording_ids[]", id);
      });

      if (spaceExternalId) {
        queryParams.append("space_external_id", spaceExternalId);
      }

      return `${HOPIN_ROOT}/recordings/api/v1/organizers/recordings?${queryParams}`;
    },
    CONTENT_HUB_EVENT_SUMMARIES: (organizationExternalId, eventExternalIds) => {
      const queryParams = new URLSearchParams();
      queryParams.append("organization_id", organizationExternalId);
      eventExternalIds.forEach((id) => {
        queryParams.append("event_external_ids[]", id);
      });

      return `${HOPIN_ROOT}/api/v1/content_hub/events?${queryParams}`;
    },
    NEXT_SERVER_GET_RECORDINGS: (recordingsIds) => {
      const params = `recordingIds=${recordingsIds.join(",")}`;

      return `${BASE_PATH}/api/recordings/get_by_ids?${params}`;
    },
    INTERNAL_GET_SELECTED_RECORDINGS: (recordingIds) => {
      const queryParams = new URLSearchParams();
      recordingIds.forEach((id) => {
        queryParams.append("recording_ids[]", id);
      });

      return `${HOPIN_ROOT}/recordings/api/v1/internal/recordings?${queryParams}`;
    },
    EVENT_SERIES: (eventExternalId: string) =>
      `${HOPIN_ROOT}/api/v1/public/events/${eventExternalId}/event_series`,
    EVENT_SERIES_EVENTS: (eventSeriesExternalId: string) =>
      `${HOPIN_ROOT}/api/v1/public/event_series/${eventSeriesExternalId}/events`,
  };

  /**
   * Select a value from configuration by key. Throws if an invalid key
   * is passed.
   * @param  {string} key The key used to access the config value.
   * @return {any}        The corresponding value.
   */
  const get = (key: string) => {
    const val = config[key];
    if (val === undefined) throw new Error(`Missing configuration key: ${key}`);
    return val;
  };

  return get;
};

/**
 * `handleAssetDomain` - compensates for lack of domain name inside asset URL coming
 * from locally hosted monolith. For example , URL like ```/system/assets/photo.jpg```,
 * will be prefixed with `HOPIN_ROOT`.
 * Otherwise, this function works like identity method and returns the same asset URL it received.
 */
export const handleAssetDomain = (assetUrl: string) => {
  if (assetUrl && assetUrl.startsWith("/system")) {
    return `${HOPIN_ROOT}${assetUrl}`;
  }

  return assetUrl;
};

export default endpoints;
